import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import api from '../services/api';
import { useTenant } from './TenantContext';
import { formatRegionLocation } from '../utils/regionLocation';

import { WarmHostOption as WarmOption } from '../types/providers';
type Result = Awaited<ReturnType<typeof api.setAttackScenario>> | null;
type ActivationLifecycle = {
  onActivating?: (active: boolean) => void;
  beforeActivate?: () => Promise<unknown>;
};
type Submit = (warmHosts?: Record<string, number>) => Promise<Result>;
type Activate = (scenarioId: string, runbookId?: string, lifecycle?: ActivationLifecycle) => Promise<Result>;

// Confirmation precedes every mutation and the caller's blocking loading state.
// A failed submit clears that state so the choices and inline retry stay usable.
function submission(scenarioId: string, runbookId?: string, lifecycle?: ActivationLifecycle): Submit {
  let prepared = false;
  return async warmHosts => {
    lifecycle?.onActivating?.(true);
    try {
      if (!prepared) {
        await lifecycle?.beforeActivate?.();
        prepared = true;
      }
      return await (warmHosts === undefined ? api.setAttackScenario(scenarioId, runbookId) :
        api.setAttackScenario(scenarioId, runbookId, warmHosts));
    } catch (error) {
      lifecycle?.onActivating?.(false);
      throw error;
    }
  };
}
const activateDirect: Activate = (scenarioId, runbookId, lifecycle) => submission(scenarioId, runbookId, lifecycle)();
const ActivationContext = createContext<Activate | null>(null);
export const useScenarioActivation = (): Activate => useContext(ActivationContext) || activateDirect;

export function ScenarioActivationProvider({ children }: { children: React.ReactNode }) {
  const { selectedTenant } = useTenant();
  const tenant = selectedTenant?.tenant_id;
  const generation = useRef(0);
  const tenantRef = useRef(tenant);
  tenantRef.current = tenant;
  const [pending, setPending] = useState<{ submit: Submit; tenant: string | undefined; options: WarmOption[]; resolve: (value: Result) => void } | null>(null);
  const pendingRef = useRef(pending);
  pendingRef.current = pending;
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    generation.current += 1;
    const current = pendingRef.current;
    if (current && current.tenant !== tenant) {
      current.resolve(null); pendingRef.current = null; setPending(null); setSaving(false); setError('');
    }
  }, [tenant]);
  useEffect(() => () => { generation.current += 1; pendingRef.current?.resolve(null); }, []);
  const activate: Activate = async (scenarioId, runbookId, lifecycle) => {
    const token = ++generation.current;
    const scope = tenantRef.current;
    const state = await api.getWarmFleetOptions(scenarioId);
    if (token !== generation.current || scope !== tenantRef.current) return null;
    const submit = submission(scenarioId, runbookId, lifecycle);
    if (state.configured || !state.options.length) return submit();
    pendingRef.current?.resolve(null);
    setCounts(Object.fromEntries(state.options.map(option => [option.region_id, option.minimum_hosts])));
    setError('');
    return new Promise<Result>(resolve => {
      const next = { submit, tenant: scope, options: state.options, resolve };
      pendingRef.current = next; setPending(next);
    });
  };
  const cancel = () => { pending?.resolve(null); pendingRef.current = null; setPending(null); };
  const start = async () => {
    if (!pending || pending.tenant !== tenantRef.current) return;
    const current = pending;
    setSaving(true); setError('');
    try {
      const result = await current.submit(counts);
      current.resolve(result);
      if (pendingRef.current === current) { pendingRef.current = null; setPending(null); }
    } catch (err: any) {
      if (pendingRef.current === current) setError(typeof err.response?.data?.detail === 'string' ? err.response.data.detail : 'Could not start the scenario. Please retry.');
    } finally { if (!pendingRef.current || pendingRef.current === current) setSaving(false); }
  };
  const invalid = pending?.options.some(option => !Number.isInteger(counts[option.region_id]) || counts[option.region_id] < 0 || counts[option.region_id] > option.max_hosts);
  return <ActivationContext.Provider value={activate}>{children}
    <Dialog open={!!pending} onClose={saving ? undefined : cancel} maxWidth="sm" fullWidth aria-labelledby="warm-host-title">
      <DialogTitle id="warm-host-title">Prepare Docker hosts</DialogTitle>
      <DialogContent><Stack spacing={2} sx={{ mt: 1 }}>
        <Typography>Choose the minimum fleet size for each Docker location. Hosts boot in parallel and stay running while this scenario is active.</Typography>
        <Alert severity="info">With a minimum of 2, the fleet starts with 2 empty hosts. As bots fill them, it grows to keep one empty spare, within regional capacity. Scaling down keeps the minimum. Set 0 for on-demand hosts only.</Alert>
        {pending?.options.map(option => <TextField key={option.region_id} type="number"
          label={`${option.provider_name} · ${formatRegionLocation(option.region)}`}
          value={counts[option.region_id] ?? 0} disabled={saving}
          onChange={event => setCounts({ ...counts, [option.region_id]: Number(event.target.value) })}
          inputProps={{ min: 0, max: option.max_hosts, step: 1 }}
          helperText={`Minimum hosts: 0–${option.max_hosts}. Actual availability depends on regional quotas.`} />)}
        <Typography variant="body2" color="text.secondary">Ending the scenario releases its warm hosts. VM providers are unchanged.</Typography>
        {error && <Alert severity="error">{error}</Alert>}
      </Stack></DialogContent>
      <DialogActions><Button disabled={saving} onClick={cancel}>Cancel</Button><Button variant="contained" disabled={saving || invalid} onClick={() => void start()}>{saving ? 'Starting…' : 'Start scenario'}</Button></DialogActions>
    </Dialog>
  </ActivationContext.Provider>;
}
